let sharedSingleCardAudio: HTMLAudioElement | null = null;
const cardAudioInstances = new Set<HTMLAudioElement>();

export const TAAS_CARD_AUDIO_ATTR = "data-taas-card-audio";

type CardAudioStateListener = (audio: HTMLAudioElement | null) => void;
const cardAudioStateListeners = new Set<CardAudioStateListener>();
let activeCardAudioElement: HTMLAudioElement | null = null;

const notifyCardAudioState = () => {
  cardAudioStateListeners.forEach((listener) => {
    listener(activeCardAudioElement);
  });
};

export const subscribeCardAudioState = (listener: CardAudioStateListener): (() => void) => {
  cardAudioStateListeners.add(listener);
  listener(activeCardAudioElement);
  return () => {
    cardAudioStateListeners.delete(listener);
  };
};

export const getActiveCardAudio = (): HTMLAudioElement | null => activeCardAudioElement;

export function isCardAudioTrackPlaying(audioUrl: string): boolean {
  const target = normalizeCardAudioSrc(audioUrl);
  if (!target) return false;

  const isPlaying = (audio: HTMLAudioElement | null) => {
    if (!audio || audio.paused || audio.ended) return false;
    return normalizeCardAudioSrc(audio.currentSrc || audio.src || "") === target;
  };

  return isPlaying(activeCardAudioElement) || isPlaying(getSharedSingleCardAudio());
}

/** Pause the active/shared element when it matches this track URL. */
export function pauseCardAudioTrackByUrl(audioUrl: string): boolean {
  const target = normalizeCardAudioSrc(audioUrl);
  if (!target) return false;

  for (const candidate of [activeCardAudioElement, getSharedSingleCardAudio()]) {
    if (!candidate || candidate.paused || candidate.ended) continue;
    if (normalizeCardAudioSrc(candidate.currentSrc || candidate.src || "") !== target) {
      continue;
    }
    candidate.pause();
    return true;
  }

  return false;
}

export function getCardAudioElementForUrl(audioUrl: string): HTMLAudioElement | null {
  const target = normalizeCardAudioSrc(audioUrl);
  if (!target) return null;

  const matches = (candidate: HTMLAudioElement | null) => {
    if (!candidate) return false;
    return normalizeCardAudioSrc(candidate.currentSrc || candidate.src || "") === target;
  };

  for (const candidate of [activeCardAudioElement, getSharedSingleCardAudio()]) {
    if (matches(candidate)) return candidate;
  }

  for (const el of cardAudioInstances) {
    if (matches(el)) return el;
  }

  if (typeof document !== "undefined") {
    const domAudios = document.querySelectorAll(`audio[${TAAS_CARD_AUDIO_ATTR}]`);
    for (let i = 0; i < domAudios.length; i += 1) {
      const el = domAudios[i];
      if (el instanceof HTMLAudioElement && matches(el)) {
        return el;
      }
    }
  }

  return null;
}

/** Seconds to skip forward/back with transport controls (matches legacy card audio UI). */
export const CARD_AUDIO_SKIP_SECONDS = 5;

export type CardAudioPlaybackSnapshot = {
  playing: boolean;
  currentTime: number;
  duration: number;
};

export function getCardAudioPlaybackSnapshot(audioUrl: string): CardAudioPlaybackSnapshot {
  const active = getCardAudioElementForUrl(audioUrl);
  if (!active) {
    return { playing: false, currentTime: 0, duration: 0 };
  }

  const duration = Number.isFinite(active.duration) && active.duration > 0 ? active.duration : 0;

  return {
    playing: isCardAudioTrackPlaying(audioUrl),
    currentTime: active.currentTime || 0,
    duration,
  };
}

/** Mirror active/shared playback position onto a page-local `<audio>` for UI + controls. */
export function syncLocalCardAudioFromTrack(audioUrl: string, local: HTMLAudioElement): void {
  if (!audioUrl) return;

  const active = getCardAudioElementForUrl(audioUrl);
  const source = active && active !== local ? active : local;
  const src = source.currentSrc || source.src || audioUrl;

  if (src) {
    ensureCardAudioSource(local, src);
  }

  if (active && active !== local) {
    if (Number.isFinite(active.currentTime)) {
      local.currentTime = active.currentTime;
    }
    local.dataset.singleCardTitle =
      active.dataset.singleCardTitle || local.dataset.singleCardTitle || "";
    local.dataset.singleCardArtist =
      active.dataset.singleCardArtist || local.dataset.singleCardArtist || "";
    local.dataset.singleCardArtwork =
      active.dataset.singleCardArtwork || local.dataset.singleCardArtwork || "";
  }
}

export function seekCardAudioTrackByUrl(audioUrl: string, timeSeconds: number): number {
  const active = getCardAudioElementForUrl(audioUrl);
  if (!active) return 0;

  const maxTime =
    Number.isFinite(active.duration) && active.duration > 0
      ? active.duration
      : Math.max(timeSeconds, active.currentTime);
  const next = Math.min(Math.max(0, timeSeconds), maxTime);
  active.currentTime = next;
  notifyCardAudioState();
  return next;
}

export function skipCardAudioTrackByUrl(audioUrl: string, deltaSeconds: number): number {
  const active = getCardAudioElementForUrl(audioUrl);
  if (!active) return 0;
  return seekCardAudioTrackByUrl(audioUrl, active.currentTime + deltaSeconds);
}

export async function toggleCardAudioTrackPlayback(
  audioUrl: string,
  local: HTMLAudioElement,
  playExclusive: (el: HTMLAudioElement) => Promise<void>
): Promise<boolean> {
  if (isCardAudioTrackPlaying(audioUrl)) {
    pauseCardAudioTrackByUrl(audioUrl);
    return false;
  }

  syncLocalCardAudioFromTrack(audioUrl, local);

  const active = getCardAudioElementForUrl(audioUrl);
  if (active && !active.ended && active.paused && active.currentTime > 0) {
    await playCardAudioWhenReady(active, audioUrl);
    if (active !== local) {
      syncLocalCardAudioFromTrack(audioUrl, local);
    }
    return true;
  }

  await playExclusive(local);
  return true;
}

/**
 * Start exclusive card audio on the persistent shared element.
 * Page-local `<audio>` nodes stay mounted for UI only so book navigation
 * does not destroy the element that is actually playing.
 */
export async function playExclusiveCardAudioTrack(
  local: HTMLAudioElement,
  audioUrl: string
): Promise<void> {
  if (!audioUrl) return;

  const shared = getSharedSingleCardAudio();
  if (!shared || shared === local) {
    pauseOtherCardAudioInstances(local);
    registerCardAudioElement(local);
    await playCardAudioWhenReady(local, audioUrl);
    return;
  }

  shared.dataset.singleCardTitle = local.dataset.singleCardTitle || "";
  shared.dataset.singleCardArtist = local.dataset.singleCardArtist || "";
  shared.dataset.singleCardArtwork = local.dataset.singleCardArtwork || "";
  registerCardAudioElement(shared);
  registerCardAudioElement(local);
  pauseOtherCardAudioInstances(shared);

  const resumeTime = local.currentTime || 0;
  if (resumeTime > 0) {
    await resumeCardAudioAt(shared, audioUrl, resumeTime);
  } else {
    await playCardAudioWhenReady(shared, audioUrl);
  }
  syncLocalCardAudioFromTrack(audioUrl, local);
}

const bindCardAudioLifecycle = (audio: HTMLAudioElement) => {
  const boundKey = "__taasCardAudioBound";
  if ((audio as HTMLAudioElement & { [boundKey]?: boolean })[boundKey]) {
    return;
  }
  (audio as HTMLAudioElement & { [boundKey]?: boolean })[boundKey] = true;

  const markActive = () => {
    activeCardAudioElement = audio;
    notifyCardAudioState();
  };

  audio.addEventListener("play", markActive);
  audio.addEventListener("pause", () => {
    if (activeCardAudioElement === audio) {
      notifyCardAudioState();
    }
  });
  audio.addEventListener("timeupdate", () => {
    if (activeCardAudioElement === audio) {
      notifyCardAudioState();
    }
  });
  audio.addEventListener("loadedmetadata", () => {
    if (activeCardAudioElement === audio) {
      notifyCardAudioState();
    }
  });
  audio.addEventListener("ended", () => {
    if (activeCardAudioElement === audio) {
      activeCardAudioElement = null;
      notifyCardAudioState();
    }
  });
};

export const getSharedSingleCardAudio = (): HTMLAudioElement | null => {
  if (typeof window === "undefined") return null;
  if (!sharedSingleCardAudio) {
    sharedSingleCardAudio = new Audio();
    sharedSingleCardAudio.preload = "metadata";
    bindCardAudioLifecycle(sharedSingleCardAudio);
  }
  return sharedSingleCardAudio;
};

export const createCardAudioInstance = (): HTMLAudioElement => {
  const audio = new Audio();
  audio.preload = "metadata";
  registerCardAudioElement(audio);
  return audio;
};

/** Track an existing `<audio>` element (e.g. rendered in JSX per experience page). */
export const registerCardAudioElement = (audio: HTMLAudioElement) => {
  if (!cardAudioInstances.has(audio)) {
    cardAudioInstances.add(audio);
    bindCardAudioLifecycle(audio);
  }
  if (typeof audio.setAttribute === "function") {
    audio.setAttribute(TAAS_CARD_AUDIO_ATTR, "1");
    const existing = audio.getAttribute("controlsList") || "";
    if (!/\bnodownload\b/i.test(existing)) {
      audio.setAttribute(
        "controlsList",
        existing.trim() ? `${existing.trim()} nodownload` : "nodownload"
      );
    }
  }
};

/** Pause and unregister without clearing `src` (safe for React-managed `<audio>`). */
export const releaseCardAudioElement = (audio: HTMLAudioElement) => {
  cardAudioInstances.delete(audio);
  if (activeCardAudioElement === audio) {
    activeCardAudioElement = null;
    notifyCardAudioState();
  }
  audio.pause();
};

/**
 * Unregister a page-local `<audio>` on navigation without stopping playback.
 * Hands off to the shared card audio element so music continues on other pages.
 */
export const detachCardAudioElementForNavigation = (audio: HTMLAudioElement) => {
  const wasPlaying = !audio.paused && !audio.ended;
  const src = audio.currentSrc || audio.src || "";
  const hasActiveProgress = Boolean(src) && audio.currentTime > 0 && !audio.ended;
  const isActiveElement = activeCardAudioElement === audio;

  cardAudioInstances.delete(audio);

  const shared = getSharedSingleCardAudio();
  const sameTrackOnShared =
    shared &&
    src &&
    normalizeCardAudioSrc(shared.currentSrc || shared.src || "") === normalizeCardAudioSrc(src) &&
    !shared.paused &&
    !shared.ended;

  if (sameTrackOnShared) {
    activeCardAudioElement = shared;
    notifyCardAudioState();
    return;
  }

  if (wasPlaying || (isActiveElement && hasActiveProgress)) {
    handoffToSharedSingleCardAudio(audio, {
      resumePlayback: wasPlaying,
    });
    return;
  }

  if (isActiveElement && !wasPlaying) {
    activeCardAudioElement = null;
    notifyCardAudioState();
  }
};

export const disposeCardAudioInstance = (audio: HTMLAudioElement) => {
  const wasPlaying = !audio.paused && !audio.ended;
  cardAudioInstances.delete(audio);

  if (activeCardAudioElement === audio) {
    activeCardAudioElement = null;
    notifyCardAudioState();
  }

  // Hand off only when leaving the card entirely (no sibling players left).
  if (wasPlaying && cardAudioInstances.size === 0) {
    handoffToSharedSingleCardAudio(audio, { resumePlayback: true });
    return;
  }

  audio.pause();
  audio.removeAttribute("src");
  audio.load();
};

export const normalizeCardAudioSrc = (src: string) => {
  if (!src) return "";
  if (typeof window === "undefined") return src;
  try {
    return new URL(src, window.location.href).href;
  } catch {
    return src;
  }
};

export const ensureCardAudioSource = (audio: HTMLAudioElement, nextUrl: string): boolean => {
  if (!nextUrl) return false;
  const normalizedNext = normalizeCardAudioSrc(nextUrl);
  const normalizedCurrent = normalizeCardAudioSrc(audio.currentSrc || audio.src || "");
  if (normalizedCurrent === normalizedNext) return false;
  audio.src = nextUrl;
  audio.load();
  return true;
};

const playCardAudioElementWhenReady = (
  audio: HTMLAudioElement,
  srcChanged: boolean
): Promise<void> => {
  if (!srcChanged && audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
    return audio.play().then(() => undefined);
  }

  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      cleanup();
      audio.play().then(resolve).catch(reject);
    }, 12000);

    const onReady = () => {
      cleanup();
      audio.play().then(resolve).catch(reject);
    };

    const onError = () => {
      cleanup();
      reject(new Error("Audio failed to load"));
    };

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      audio.removeEventListener("canplay", onReady);
      audio.removeEventListener("loadedmetadata", onReady);
      audio.removeEventListener("error", onError);
    };

    if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      cleanup();
      audio.play().then(resolve).catch(reject);
      return;
    }

    audio.addEventListener("canplay", onReady);
    audio.addEventListener("loadedmetadata", onReady);
    audio.addEventListener("error", onError);
  });
};

/** Wait until the element can play (needed for AAC / slow CDN metadata). */
export const playCardAudioWhenReady = (audio: HTMLAudioElement, nextUrl: string): Promise<void> => {
  const srcChanged = ensureCardAudioSource(audio, nextUrl);
  return playCardAudioElementWhenReady(audio, srcChanged);
};

/** Resume shared handoff playback at a specific position after source is ready. */
const resumeCardAudioAt = (
  audio: HTMLAudioElement,
  nextUrl: string,
  timeSeconds: number
): Promise<void> => {
  const srcChanged = ensureCardAudioSource(audio, nextUrl);

  const seekAndPlay = () => {
    if (Number.isFinite(timeSeconds) && timeSeconds > 0) {
      audio.currentTime = timeSeconds;
    }
    return audio.play().then(() => undefined);
  };

  if (!srcChanged && audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
    return seekAndPlay();
  }

  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      cleanup();
      seekAndPlay().then(resolve).catch(reject);
    }, 12000);

    const onReady = () => {
      cleanup();
      seekAndPlay().then(resolve).catch(reject);
    };

    const onError = () => {
      cleanup();
      reject(new Error("Audio failed to load"));
    };

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      audio.removeEventListener("canplay", onReady);
      audio.removeEventListener("loadedmetadata", onReady);
      audio.removeEventListener("error", onError);
    };

    if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      cleanup();
      seekAndPlay().then(resolve).catch(reject);
      return;
    }

    audio.addEventListener("canplay", onReady);
    audio.addEventListener("loadedmetadata", onReady);
    audio.addEventListener("error", onError);
  });
};

export const pauseOtherCardAudioInstances = (current: HTMLAudioElement) => {
  cardAudioInstances.forEach((el) => {
    if (el !== current && !el.paused) {
      el.pause();
    }
  });

  if (typeof document !== "undefined") {
    document.querySelectorAll(`audio[${TAAS_CARD_AUDIO_ATTR}]`).forEach((el) => {
      if (el !== current && el instanceof HTMLAudioElement && !el.paused) {
        el.pause();
      }
    });
  }

  if (sharedSingleCardAudio && sharedSingleCardAudio !== current && !sharedSingleCardAudio.paused) {
    sharedSingleCardAudio.pause();
  }
};

export const handoffToSharedSingleCardAudio = (
  source: HTMLAudioElement,
  options?: { resumePlayback?: boolean }
) => {
  if (typeof window === "undefined") return;

  const shared = getSharedSingleCardAudio();
  if (!shared || shared === source) return;
  if (source.ended) return;

  const src = source.currentSrc || source.src;
  if (!src) return;

  const resumePlayback = options?.resumePlayback ?? (!source.paused && !source.ended);

  const normalizeSrc = (value: string) => {
    if (!value) return "";
    try {
      return new URL(value, window.location.href).href;
    } catch {
      return value;
    }
  };

  const nextSrc = normalizeSrc(src);
  const resumeTime = source.currentTime;

  shared.dataset.singleCardTitle = source.dataset.singleCardTitle || "";
  shared.dataset.singleCardArtist = source.dataset.singleCardArtist || "";
  shared.dataset.singleCardArtwork = source.dataset.singleCardArtwork || "";

  activeCardAudioElement = shared;
  notifyCardAudioState();

  if (resumePlayback) {
    void resumeCardAudioAt(shared, src, resumeTime)
      .then(() => {
        source.pause();
      })
      .catch(() => {
        if (activeCardAudioElement === shared && shared.paused) {
          activeCardAudioElement = null;
          notifyCardAudioState();
        }
      });
    return;
  }

  source.pause();

  const currentSharedSrc = normalizeSrc(shared.currentSrc || shared.src || "");
  if (currentSharedSrc !== nextSrc) {
    ensureCardAudioSource(shared, src);
  }
  if (Number.isFinite(resumeTime) && resumeTime > 0) {
    shared.currentTime = resumeTime;
  }
};

/** Pause every other TAASCARD preview `<audio>` so only one plays at a time. */
export const pauseOtherTaasCardAudios = (current: HTMLAudioElement) => {
  pauseOtherCardAudioInstances(current);
};

export const pauseActiveCardAudio = () => {
  const active = activeCardAudioElement;
  if (active) {
    active.pause();
    active.currentTime = 0;
    activeCardAudioElement = null;
    notifyCardAudioState();
    return;
  }

  const shared = getSharedSingleCardAudio();
  if (shared) {
    shared.pause();
    shared.currentTime = 0;
  }
};
